import { useCallback, useContext, useEffect, useState } from "react";
import { AdministrarContext } from "./context";
import { Icon, Tooltip } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DataTable from "components/Tables/Table-v2";
import { formatMoneyPunto } from "utils/formatters";
import SinDatos from "components/Carteles/SinDatos";
import Loading from "components/Carteles/Loading";
import ModalProvision from "./ModalProvision";
import ModalNuevaProvision from "./ModalNuevaProvision";
import ModalProvisionSeleccion from "./ModalProvisionSeleccion";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";
import SoftBadge from "components/SoftBadge";
import { formatDate } from "utils/formatters";
import { PASOS } from "./pasosCircuito";

const PARTIAL_CLOSE_STATE = "Cerrado con entrega parcial";
const COMPLETED_WITH_PARTIAL_DELIVERY_STATE = "Completada con entrega parcial";
const COMPLETED_WITHOUT_DELIVERIES_STATE = "Completada sin entregas";

export default function Tab4() {
  const { data, loading, refetch, mode } = useContext(AdministrarContext);
  const { user, verificarAcceso } = useContext(UserContext);
  const [selectedProvision, setSelectedProvision] = useState(null);
  const [columns] = useState([
    { name: "id", desc: "Código", width: "1%", align: "center" },
    { name: "proveedor", desc: "Proveedor", align: "left" },
    { name: "fechaVencimiento", desc: "vencimiento", align: "center" },
    { name: "estado", desc: "Estado", align: "center" },
    { name: "total", desc: "Total", align: "right" },
    { name: "enviado", desc: "Enviado", width: "1%", align: "center" },
  ]);
  const [openModalProvision, setOpenModalProvision] = useState(false);
  // Renglones con saldo y sin orden activa: los que tienen cotizaciones se ordenan desde
  // "Crear Ordenes" (Concurso); el resto, con "Orden Directa".
  const [renglonesConCotizacion, setRenglonesConCotizacion] = useState([]);
  const [renglonesParaOrdenDirecta, setRenglonesParaOrdenDirecta] = useState([]);
  const [openModalNuevaProvision, setOpenModalNuevaProvision] = useState(false);
  const [openModalProvisionSeleccion, setOpenModalProvisionSeleccion] = useState(false);

  useEffect(() => {
    if (data.detalles?.length > 0) {
      const idsProvisionesActivas = [];
      const recibidoPorDetalle = new Map();
      data.provisiones
        .filter((p) => !p.anulada && p.estado !== PARTIAL_CLOSE_STATE)
        .forEach((provision) => {
          provision.detalles.forEach((detalle) => {
            idsProvisionesActivas.push(detalle.idPedidoCompraDetalle);
          });
        });
      data.provisiones.forEach((provision) => {
        provision.detalles.forEach((detalle) => {
          const idDetalle = detalle.idPedidoCompraDetalle;
          recibidoPorDetalle.set(
            idDetalle,
            (recibidoPorDetalle.get(idDetalle) || 0) + (Number(detalle.cantidadEntregada) || 0)
          );
        });
      });
      const detallesConSaldo = data.detalles.map((detalle) => ({
        ...detalle,
        cantidadPendienteProvision: Math.max(
          (Number(detalle.cantidadVerificada) || 0) -
            (recibidoPorDetalle.get(detalle.id) || 0),
          0
        ),
      }));
      setRenglonesConCotizacion(
        detallesConSaldo.filter(
          (detalle) =>
            !idsProvisionesActivas.includes(detalle.id) &&
            detalle.cantidadPendienteProvision > 0.000001 &&
            detalle.cotizaciones?.length > 0
        )
      );
      setRenglonesParaOrdenDirecta(
        detallesConSaldo.filter(
          (detalle) =>
            !idsProvisionesActivas.includes(detalle.id) &&
            detalle.cantidadPendienteProvision > 0.000001 &&
            (detalle.cotizaciones?.length === 0 || !detalle.cotizacionSeleccionada)
        )
      );
    }
  }, [data]);

  const getEstadoColor = useCallback((estado) => {
    switch (estado) {
      case "Pendiente":
        return "secondary";
      case "Vencida":
        return "error";
      case "Anulada":
        return "error";
      case PARTIAL_CLOSE_STATE:
      case COMPLETED_WITH_PARTIAL_DELIVERY_STATE:
      case COMPLETED_WITHOUT_DELIVERIES_STATE:
        return "dark";
      default:
        return "info";
    }
  }, []);

  const getRows = useCallback((data, handleAction) => {
    return data?.map((item, index) => {
      return {
        hasBorder: true,
        id: (
          <SoftBox sx={{ cursor: "pointer" }} onClick={() => handleAction(item)}>
            {item.id}
          </SoftBox>
        ),
        proveedor: (
          <SoftBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: "pointer" }}
            onClick={() => handleAction(item)}
          >
            <Icon fontSize="small">person</Icon>
            <SoftTypography variant="caption" fontWeight="bold">
              {[
                item?.proveedor?.razonSocial,
                item?.proveedor?.nombre,
                item?.proveedor?.apellido,
              ].join(" ")}
            </SoftTypography>
          </SoftBox>
        ),
        fechaVencimiento: (
          <SoftBox sx={{ cursor: "pointer" }} onClick={() => handleAction(item)}>
            {item.fechaVencimiento ? formatDate(item.fechaVencimiento, "dd/MM/yy") : ""}
          </SoftBox>
        ),
        estado: (
          <SoftBox sx={{ cursor: "pointer" }} onClick={() => handleAction(item)}>
            <SoftBadge
              badgeContent={item.estado}
              color={getEstadoColor(item.estado)}
            />
          </SoftBox>
        ),
        enviado: (
          <SoftBox
            sx={{ cursor: "pointer" }}
            onClick={() => handleAction(item)}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {item.enviado ? (
              <Tooltip
                title={
                  <SoftBox>
                    <SoftTypography variant="caption" color="white" fontWeight="bold">
                      Email enviado
                    </SoftTypography>
                    <br />
                    <SoftTypography variant="caption" color="white">
                      Fecha: {item.fechaEnvio ? formatDate(item.fechaEnvio, "dd/MM/yyyy hh:mm") : "No disponible"}
                    </SoftTypography>
                    {item.usuarioQueEnvio && (
                      <>
                        <br />
                        <SoftTypography variant="caption" color="white">
                          Por: {item.usuarioQueEnvio.nombre} {item.usuarioQueEnvio.apellido}
                        </SoftTypography>
                      </>
                    )}
                  </SoftBox>
                }
                placement="top"
                arrow
              >
                <Icon
                  sx={{
                    color: "success.main",
                    fontSize: "18px"
                  }}
                >
                  check_circle
                </Icon>
              </Tooltip>
            ) : (
              <Tooltip title="Email no enviado" placement="top">
                <Icon
                  sx={{
                    color: "text.disabled",
                    fontSize: "18px"
                  }}
                >
                  radio_button_unchecked
                </Icon>
              </Tooltip>
            )}
          </SoftBox>
        ),
        total: (
          <SoftBox sx={{ cursor: "pointer" }} onClick={() => handleAction(item)}>
            ${formatMoneyPunto(item.total || 0)}
          </SoftBox>
        ),
      };
    });
  }, []);

  const allowNewProvisionDirecta = () => {
    if (mode === "Lectura" || !tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.CREAR_ORDEN_DIRECTA)) return false;

    if (!(
      data?.estados?.some(e => e.base.nombre === PASOS.ORDEN_PROVISION) ||
      data?.estadoBase?.nombre === PASOS.ORDEN_PROVISION
    )) {
      return false;
    }

    if (
      data?.tipoCompra === "Directa" &&
      (data?.stockEstado === "Verificado" || data?.tipoDetalle == "Servicios")
    ) {
      return true;
    }
    if (
      data?.tipoCompra != "Directa" &&
      data?.licitacionEstado === "Finalizada" &&
      renglonesParaOrdenDirecta.length > 0
    ) {
      return true;
    }
    return false;
  };

  const allowNewProvisionSeleccion = () => {
    if (mode === "Lectura" || !tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.GENERAR_ORDENES)) return false;

    if (!(
      data?.estados?.some(e => e.base.nombre === PASOS.ORDEN_PROVISION) ||
      data?.estadoBase?.nombre === PASOS.ORDEN_PROVISION
    )) {
      return false;
    }

    if (
      data?.tipoCompra != "Directa" &&
      renglonesConCotizacion.length > 0 &&
      ["Cerrada", "Finalizada"].includes(data?.licitacionEstado)
    ) {
      return true;
    }
    return false;
  };

  return (
    <SoftBox m={1}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center">
        <SoftTypography variant="h6">Ordenes de Provisión</SoftTypography>
        <SoftBox display="flex" gap={1}>
          {allowNewProvisionDirecta() ? (
            <SoftButton
              variant="contained"
              color="uproGreen"
              circular
              onClick={() => setOpenModalNuevaProvision(true)}
            >
              <Icon>add</Icon>
              Orden Directa
            </SoftButton>
          ) : null}

          {allowNewProvisionSeleccion() ? (
            <SoftButton
              variant="contained"
              color="uproPink"
              circular
              onClick={() => setOpenModalProvisionSeleccion(true)}
            >
              <Icon>add</Icon>
              Crear Ordenes
            </SoftButton>
          ) : null}
        </SoftBox>
      </SoftBox>
      <SoftBox>
        {loading ? (
          <Loading />
        ) : data?.provisiones?.length > 0 ? (
          <DataTable
            columns={columns}
            rows={getRows(data?.provisiones || [], (item) => {
              setSelectedProvision(item);
              setOpenModalProvision(true);
            })}
          />
        ) : (
          <SinDatos />
        )}
      </SoftBox>
      <ModalProvision
        open={openModalProvision}
        handleClose={() => {
          setSelectedProvision(null);
          setOpenModalProvision(false);
        }}
        id={selectedProvision?.id}
        refetchParent={refetch}
        mode={mode}
      />
      <ModalNuevaProvision
        open={openModalNuevaProvision}
        handleClose={() => setOpenModalNuevaProvision(false)}
        productosSinProvision={renglonesParaOrdenDirecta}
        refetchParent={refetch}
        idPedido={data.id}
        tipoCompra={data.tipoCompra}
      />
      <ModalProvisionSeleccion
        open={openModalProvisionSeleccion}
        handleClose={() => setOpenModalProvisionSeleccion(false)}
        refetchParent={refetch}
        idPedido={data.id}
        productosSinProvision={renglonesConCotizacion}
      />
    </SoftBox>
  );
}
